import { Injectable, NgZone, inject } from '@angular/core'

import { Actions, createEffect, ofType } from '@ngrx/effects'

import { Observable, catchError, endWith, map, of, switchMap, takeUntil } from 'rxjs'

import { WorkflowBuilderActions } from './workflow-builder.actions'

import { AiIntegratorApi } from '../../../new-ai/agents/integrator/api/integrator.api'

function runInZone<T>(zone: NgZone) {
  return (source: Observable<T>) =>
    new Observable<T>((observer) =>
      source.subscribe({
        next: (value) => zone.run(() => observer.next(value)),
        error: (error) => zone.run(() => observer.error(error)),
        complete: () => zone.run(() => observer.complete()),
      })
    )
}

@Injectable()
export class WorkflowBuilderEffects {
  #actions$ = inject(Actions)
  #integratorApi = inject(AiIntegratorApi)
  #ngZone = inject(NgZone)

  buildWorkflowResponse = createEffect(
    () =>
      this.#actions$.pipe(
        ofType(WorkflowBuilderActions.sendMessage),
        switchMap(
          ({
            message,
            model,
            thread_id,
            decision,
            tool_name,
            feedback,
            resource_id,
            resource_definition_id,
            context,
          }) =>
            this.#integratorApi
              .getBuildWorkflowResponse({
                query: message,
                model: model ?? undefined,
                thread_id: thread_id ?? undefined,
                decision: decision ?? undefined,
                tool_name: tool_name ?? undefined,
                feedback: feedback ?? undefined,
                resource_id: resource_id ?? undefined,
                resource_definition_id: resource_definition_id ?? undefined,
                context: context ?? undefined,
              })
              .pipe(
                runInZone(this.#ngZone),
                map((response) => WorkflowBuilderActions.getResponseStream({ response })),
                takeUntil(this.#actions$.pipe(ofType(WorkflowBuilderActions.cancelPendingRequest))),
                catchError((error) =>
                  of(
                    WorkflowBuilderActions.getResponseFailure({
                      error: error?.message || 'Failed to build workflow',
                    })
                  )
                ),
                endWith(WorkflowBuilderActions.getResponseStreamCompleted())
              )
        )
      ),
    { dispatch: true }
  )
}
